// @ts-nocheck
import React, { useState } from 'react';
import { Table, getVisibleColumns } from '../../../common/Table';
import SlideoutRequestDetail from '../../slideout/RequestDetails';
import SlideoutCopyRequest from '../../slideout/CopyRequest';

import Loading from '../../../common/Loading';
import KwPanel from '../../../common/KwPanel';
import RequestDetailsSignatures from '../../slideout/RequestDetailsSignatures';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../../hooks/useTableSettings.ts';
import SlidingPane from 'react-sliding-pane';
import RequestsColumnsConfig from './RequestsColumnsConfig';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  exportAssignedRequestData,
  exportRequestData,
  getRequestById,
  getRequests,
} from '../../../../api/requestApi';
import { useAppSelector } from '../../../../hooks';

const allColumns = [
  'reference',
  'games',
  'platform',
  'companyName',
  'requestedByCompanyName',
  'currentRequestValue',
  'numberOfKeys',
  'keyWorkflowName',
  'requestedByFullName',
  'requestedDate',
  'requiredByDate',
  'requestCompletionDate',
  'currentUserProfileGroupName',
  'requestStatus',
  'assignedToUserProfileFullName',
];

const suffixColumns = ['checkbox'];
function getDefaultVisibleColumns() {
  return allColumns.filter(
    (x) => x !== 'requestedByCompanyName' && x !== 'requestCompletionDate'
  );
}

export interface RequestsListProps {
  reload?: unknown;
  assignedToMe?: boolean;
  handleDownloadKeys: (request: unknown) => void;
  handleUploadKeys: (request: unknown) => void;
  handleBulkStatusChange: (request: unknown) => void;
  handleBulkReassign: (request: unknown) => void;
  handleBulkSteamRequest: (request: unknown) => void;
  isProcessing: boolean;
  itemsChecked: unknown[];
  setItemsChecked: (checked: unknown[]) => void;
  handleBulkActions: (open: boolean) => void;
  handleReturnKeys: (request: unknown) => void;
  bulkActionTitle: string;
  tableViewSwitcher: Record<string, unknown>;
  setCompleteRequestSlideoutOpen: (open: boolean) => void;
  setBreadcrumb?: (breadcrumb: unknown) => void;
  scope?: Record<string, unknown>;
  settingsKey?: string;
  title?: string;
}

function RequestsList({
  reload,
  assignedToMe,
  handleDownloadKeys,
  handleUploadKeys,
  handleBulkStatusChange,
  handleBulkReassign,
  handleBulkSteamRequest,
  isProcessing,
  itemsChecked,
  setItemsChecked,
  handleBulkActions,
  handleReturnKeys,
  bulkActionTitle,
  tableViewSwitcher,
  setCompleteRequestSlideoutOpen,
  setBreadcrumb,
  scope,
  settingsKey,
  title,
}: RequestsListProps) {
  const { t } = useTranslation();

  const currentUser = useAppSelector((state) => state.auth);

  const [requests, setRequests] = useState();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const defaultVisibleColumns = getDefaultVisibleColumns();

  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filterChange,
    isLoading,
    filters,
    filtersChange,
  } = useTableSettings(
    settingsKey || 'requestlist-settings',
    getTableSettingsDefaults({
      defaultSort: 'reference',
      defaultVisibleColumns,
    }),
    { assignedToMe: assignedToMe || false },
    getRequests,
    setRequests,
    reload,
    t('Failed to load requests'),
    scope
  );

  // slideout panel
  const [detailsSlideoutOpen, setDetailsSlideoutOpen] = useState(false);
  const [detailsSlideoutItem, setDetailsSlideoutItem] = useState();
  const [signaturesSlideoutOpen, setSignaturesSlideoutOpen] = useState(false);
  const [signaturesSlideoutItem, setSignaturesSlideoutItem] = useState();
  const [copyRequestSlideoutOpen, setCopyRequestSlideoutOpen] = useState(false);
  const [copyRequestSlideoutRequests, setCopyRequestSlideoutRequests] =
    useState();
  const [copyRequestSlideoutRequestGroup, setCopyRequestSlideoutRequestGroup] =
    useState();

  const handleCopyRequest = (e, request) => {
    e.cancelBubble = true;
    setCopyRequestSlideoutOpen(true);
    getRequestById(request.requestGroupId).then((d) => {
      setCopyRequestSlideoutRequests([request]);
      setCopyRequestSlideoutRequestGroup(d);
    });
  };

  const handleShowDetails = (e, request) => {
    e.cancelBubble = true;
    setDetailsSlideoutItem(request);
    setDetailsSlideoutOpen(true);
  };

  let handleShowSignatures = (e, requestStep) => {
    setSignaturesSlideoutItem(requestStep);
    setSignaturesSlideoutOpen(true);
  };

  // The base config of the columns, this will be filtered based on the visible (above)
  const columnsConfig = RequestsColumnsConfig({
    params,
    requests,
    currentUser,
    filterChange,
    filtersChange,
    itemsChecked,
    setItemsChecked,
    handleShowDetails,
    handleDownloadKeys,
    handleUploadKeys,
    handleBulkStatusChange,
    handleBulkReassign,
    handleBulkSteamRequest,
    handleCopyRequest,
    handleReturnKeys,
    isProcessing,
  });

  const shouldRowUpload = (row) =>
    row.allowUploadKeys && !row.isUpdateInProgress && !isProcessing;

  const handleRowFileUpload = (row, file) => {
    handleUploadKeys(null, { ...row, file });
  };

  // filter
  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;

  const suffixColumnsWithConfig = getVisibleColumns(suffixColumns, columnsConfig);
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  // return an alaska table
  return requests?.data ? (
    <>
      <KwPanel className="mb-4">
        {title && (
          <header className="mb-2">
            <h2 className="no-margin">{title}</h2>
          </header>
        )}
        <Table
          columns={columns}
          suffixColumns={suffixColumnsWithConfig}
          allColumns={allColumns}
          defaultVisibleColumns={defaultVisibleColumns}
          rows={requests.data.map((x) => {
            return { Key: x.id, ...x };
          })}
          totalRowCount={requests.totalRowCount}
          filteredRowCount={requests.filteredRowCount}
          scopes={requests.scopes}
          tableSettings={tableSettings}
          setTableSettings={setTableSettings}
          isLoading={isLoading}
          filters={filters}
          filterChange={filterChange}
          className="td-align-top"
          bulkActionTitle={bulkActionTitle}
          itemsChecked={itemsChecked}
          setBulkActionsSlideoutOpen={handleBulkActions}
          tableActionBarButtons={tableViewSwitcher}
          exportData={
            assignedToMe ? exportAssignedRequestData : exportRequestData
          }
          setBreadcrumb={setBreadcrumb}
          hideScopeTags={scope}
          shouldRowUpload={shouldRowUpload}
          handleRowFileUpload={handleRowFileUpload}
        />
      </KwPanel>

      <SlidingPane
        isOpen={detailsSlideoutOpen}
        hideHeader={true}
        from="right"
        className="side-panel"
        onRequestClose={() => setDetailsSlideoutOpen(false)}
      >
        <>
          {detailsSlideoutItem && (
            <SlideoutRequestDetail
              requestGroupId={detailsSlideoutItem.requestGroupId}
              requestId={detailsSlideoutItem.id}
              done={(d) => {
                setDetailsSlideoutOpen(false);
              }}
              cancel={() => setDetailsSlideoutOpen(false)}
              onShowSignatures={handleShowSignatures}
            />
          )}
        </>
      </SlidingPane>

      <SlidingPane
        isOpen={signaturesSlideoutOpen}
        hideHeader={true}
        from="right"
        className="small-side-panel"
        onRequestClose={() => setSignaturesSlideoutOpen(false)}
      >
        <>
          {signaturesSlideoutItem && (
            <RequestDetailsSignatures
              done={(d) => {
                setSignaturesSlideoutOpen(false);
              }}
              cancel={() => setSignaturesSlideoutOpen(false)}
              requestStep={signaturesSlideoutItem}
            />
          )}
        </>
      </SlidingPane>

      <SlidingPane
        isOpen={copyRequestSlideoutOpen}
        hideHeader={true}
        from="right"
        className="xlarge-side-panel"
        onRequestClose={() => setCopyRequestSlideoutOpen(false)}
      >
        <>
          {copyRequestSlideoutRequestGroup && copyRequestSlideoutRequests ? (
            <SlideoutCopyRequest
              done={(d) => {
                setCopyRequestSlideoutOpen(false);
              }}
              cancel={() => setCopyRequestSlideoutOpen(false)}
              requestGroup={copyRequestSlideoutRequestGroup}
              requests={copyRequestSlideoutRequests}
              currentUser={currentUser}
              setCompleteRequestSlideoutOpen={setCompleteRequestSlideoutOpen}
            />
          ) : (
            <Loading></Loading>
          )}
        </>
      </SlidingPane>
    </>
  ) : (
    <Loading></Loading>
  );
}

export default RequestsList;
